// Console command registry. A command is `name + help + run(ctx)`; the
// console input row tokenises a submitted line, looks the command up here,
// and invokes it. Registry is a module-level Map so additional commands
// can be registered from anywhere via register().

import { INTENTS } from '@store/intents';
import type { Store } from '@store/store';
import type { State } from '@store/schema';
import type { ViewerContext } from '@app/context';
import { clearBuffer, getBuffer, log, type LogLevel } from './logger';

export interface CommandCtx {
    log: (level: LogLevel, msg: string) => void;
    args: string[];
    raw: string;
}
export type CommandFn = (ctx: CommandCtx) => void | Promise<void>;
export interface CommandSpec { name: string; help: string; run: CommandFn; }

const registry = new Map<string, CommandSpec>();

export function register(spec: CommandSpec): void { registry.set(spec.name, spec); }
export function lookup(name: string): CommandSpec | undefined { return registry.get(name); }
export function listCommands(): CommandSpec[] {
    return [...registry.values()].sort((a, b) => a.name.localeCompare(b.name));
}

let builtinsRegistered = false;
export function registerBuiltins(viewer: ViewerContext, store: Store<State>): void {
    if (builtinsRegistered) return;
    builtinsRegistered = true;

    register({
        name: 'help',
        help: 'list available commands',
        run: ({ log }) => {
            const cmds = listCommands();
            log('info', `commands (${cmds.length}):`);
            for (const c of cmds) log('info', `  ${c.name.padEnd(12)} ${c.help}`);
        },
    });

    register({
        name: 'clear',
        help: 'clear the console buffer',
        run: () => { clearBuffer(); },
    });

    register({
        name: 'echo',
        help: 'print the rest of the line',
        run: ({ log, args }) => { log('info', args.join(' ')); },
    });

    register({
        name: 'history',
        help: 'show recent submitted commands',
        run: ({ log }) => {
            const cmds = getBuffer().filter((e) => e.level === 'cmd').slice(-20);
            if (!cmds.length) { log('info', '(no history)'); return; }
            for (const e of cmds) log('info', `  ${e.msg}`);
        },
    });

    register({
        name: 'debug',
        help: 'usage: debug on|off — toggle viewer.sceneMeta.debugEnabled',
        run: ({ log, args }) => {
            const arg = (args[0] || '').toLowerCase();
            if (arg !== 'on' && arg !== 'off') {
                log('error', `usage: debug on|off (current: ${viewer.sceneMeta.debugEnabled ? 'on' : 'off'})`);
                return;
            }
            viewer.sceneMeta.debugEnabled = (arg === 'on');
            log('info', `debug ${arg}`);
        },
    });

    register({
        name: 'theme',
        help: 'usage: theme cycle',
        run: ({ log, args }) => {
            const arg = (args[0] || '').toLowerCase();
            if (arg !== 'cycle') { log('error', 'usage: theme cycle'); return; }
            store.dispatch(INTENTS.CYCLE_THEME);
        },
    });

    register({
        name: 'screenshot',
        help: 'save a screenshot of the current view',
        run: () => { store.dispatch(INTENTS.UI_SCREENSHOT_SAVE); },
    });
}

// Tokenise a submitted line into [command, ...args]. Whitespace-split only;
// quoted strings are not parsed (YAGNI for the foundation).
export function tokenise(line: string): { name: string; args: string[] } {
    const parts = line.trim().split(/\s+/);
    const name = parts.shift() || '';
    return { name, args: parts };
}

export async function runLine(raw: string): Promise<void> {
    const trimmed = raw.trim();
    if (!trimmed) return;
    log('cmd', trimmed);
    const { name, args } = tokenise(trimmed);
    const spec = lookup(name);
    if (!spec) {
        log('error', `unknown command: ${name}. Type 'help' for a list.`);
        return;
    }
    try {
        await spec.run({ log, args, raw: trimmed });
    } catch (e) {
        log('error', e instanceof Error ? e.message : String(e));
    }
}
